import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { LeadSection } from "@/components/LeadSection";
import { SiteFooter } from "@/components/SiteFooter";

const TITLE = "Arbeitsweise: So läuft ein Shopify-Projekt bei mir ab";
const DESCRIPTION =
  "Von der Anfrage über Umfang und Angebot bis zur Umsetzung in der Theme-Kopie und Freigabe: Meine Arbeitsweise für Shop-Erstellung, Theme-Anpassungen und Conversion-Optimierung.";

const principles = [
  {
    title: "Umfang vor Preis",
    text: "Vor dem Angebot klären wir schriftlich, was gebaut oder geändert wird. Ohne klaren Umfang gibt es kein seriöses Angebot, weder als Festpreis noch als Stundenschätzung.",
  },
  {
    title: "Zielbild und Messgröße",
    text: "Wir legen vor dem Start fest, wie der Zustand nach dem Projekt aussieht und woran wir ihn messen. Bei der Conversion-Optimierung sind das Kennzahlen wie Kaufabschlussrate oder Umsatz pro Session.",
  },
  {
    title: "Abrechnung je nach Aufgabe",
    text: "Theme-Anpassungen und Entwicklung rechne ich mit 110 € netto pro Stunde ab, mit Aufwandsschätzung im Voraus. Shop-Aufbau und Migration gibt es zum Festpreis nach Umfang, Conversion-Programme ab 3.990 € netto.",
  },
  {
    title: "Diagnose vor Maßnahme",
    text: "Ich schlage keine Maßnahmen vor, bevor ich Shop, Daten und Mobile-Erlebnis gesehen habe. Was ich im Detail ansehe, hängt vom Projekt ab: Technik und Sortiment beim Aufbau, Funnel und Zahlen bei der Optimierung.",
  },
  {
    title: "Kein Gratis-Pitch",
    text: "Ich baue keine kostenlosen Konzepte, Mockups oder Testpläne, um ein Projekt zu gewinnen. Die kostenlose Ersteinschätzung ist ehrlich und nützlich, aber sie ist eine Einschätzung, keine Arbeitsprobe auf Verdacht.",
  },
  {
    title: "Nein sagen gehört dazu",
    text: "Wenn eine Maßnahme sich nicht rechnet oder ein anderer Schritt wichtiger wäre, sage ich das vor dem Angebot. Ein Projekt, das nicht passt, schadet uns beiden.",
  },
];

const conversations = [
  {
    no: "01",
    title: "Anfrage",
    text: "Sie schildern kurz Ihr Vorhaben: neuer Shop, Anpassungen am bestehenden Theme oder Conversion-Optimierung. Ich gebe eine ehrliche Ersteinschätzung, ob und wie ich helfen kann.",
  },
  {
    no: "02",
    title: "Umfang und Aufwand",
    text: "Wir klären Umfang, Zuständigkeiten und Rahmenbedingungen. Daraus ergibt sich der Aufwand und die passende Abrechnungsform: Stunde, Festpreis oder Programm.",
  },
  {
    no: "03",
    title: "Angebot",
    text: "Schriftlich und konkret: Umfang, Zeitrahmen, was ich brauche, was nicht enthalten ist. Sie entscheiden in Ruhe, ohne Verkaufsdruck.",
  },
  {
    no: "04",
    title: "Umsetzung, Freigabe, Auswertung",
    text: "Ich arbeite in einer Theme-Kopie oder einem Testshop, Sie geben frei, dann geht es live. Wo es sinnvoll ist, werten wir die Wirkung danach anhand der vereinbarten Kennzahlen aus, abhängig von der Datenlage.",
  },
];

const notIncluded = [
  "Kostenlose Konzepte oder Mockups vor Auftragserteilung",
  "Redesigns, die nur anders aussehen sollen",
  "Endlose Änderungsschleifen ohne Messgröße",
  "App-Installationen, die das Problem nur überdecken",
  "Berichte, die niemand liest",
];

export const Route = createFileRoute("/methode")({
  component: MethodePage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://shopify-freelancer.net/methode" },
      { property: "og:type", content: "article" },
    ],
    links: [
      { rel: "canonical", href: "https://shopify-freelancer.net/methode" },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/methode" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/method" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/methode" },
    ],
  }),
});

function MethodePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section className="veil border-b border-border/60 py-14 md:py-20">
          <div className="mx-auto max-w-3xl px-5">
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Methode
            </p>
            <h1 className="mt-5 text-4xl font-bold leading-tight md:text-5xl">
              Klarer Ablauf, klare Kosten, <span className="text-signal">saubere Umsetzung</span>.
            </h1>
            <p className="mt-6 leading-relaxed text-muted-foreground">
              Ob Shop-Erstellung, Theme-Anpassung oder Conversion-Optimierung: Jedes
              Projekt bei mir läuft nach dem gleichen einfachen Muster. Erst Umfang und
              Aufwand klären, dann ein Angebot, dann Umsetzung in einer sicheren
              Arbeitskopie mit Ihrer Freigabe, danach dort, wo es sinnvoll ist, die
              Auswertung.
            </p>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Sechs Prinzipien</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {principles.map((p) => (
                <div
                  key={p.title}
                  className="shadow-lift rounded-lg border border-border bg-card p-6"
                >
                  <h3 className="text-lg font-semibold">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {p.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">
              Vier Schritte von der Anfrage zum Ergebnis
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Kein Verkaufsprozess, sondern ein überschaubarer Ablauf. Nach Schritt zwei
              wissen Sie, was das Projekt umfasst und was es kostet, unabhängig davon, ob
              ich den Auftrag bekomme.
            </p>
            <div className="mt-10 grid gap-5 md:grid-cols-4">
              {conversations.map((c) => (
                <div key={c.no} className="rounded-lg border border-border bg-card p-6">
                  <span className="text-signal font-display text-4xl font-bold">
                    {c.no}
                  </span>
                  <h3 className="mt-4 text-base font-semibold">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {c.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-16">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 md:grid-cols-2">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">
                Wie sich der Aufwand zusammensetzt
              </h2>
              <div className="mt-5 space-y-4 leading-relaxed text-muted-foreground">
                <p>
                  Je nach Aufgabe abrechnen wir unterschiedlich: Theme-Anpassungen und
                  Entwicklung zu 110 € netto pro Stunde mit Aufwandsschätzung im Voraus,
                  Shop-Aufbau und Migration zum Festpreis nach Umfang, Conversion-Programme
                  ab 3.990 € netto. In jedem Fall wissen Sie vor dem Start, was es kostet.
                </p>
                <p>
                  Was eine Conversion-Verbesserung wirtschaftlich bedeuten kann, zeigt ein
                  Beispiel, kein Versprechen: Bei einem Shop mit 25.000 Sessions, 1,4 %
                  Conversion-Rate und 78 € Warenkorb würde ein Anstieg auf 2,1 % rund
                  13.600 € zusätzlichen Monatsumsatz bedeuten. Ob und wie stark sich Ihre
                  Rate verbessert, hängt von Ausgangslage, Traffic und Datenlage ab und
                  lässt sich nicht garantieren.
                </p>
                <p>
                  Genau dafür gibt es den Rechner: Sie können mit Ihren eigenen Zahlen
                  verschiedene Szenarien durchrechnen, bevor wir sprechen.
                </p>
              </div>
              <Link
                to="/roi-rechner"
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary"
              >
                Zum Conversion-Rechner
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 md:p-8">
              <h3 className="text-lg font-semibold">Was nicht dazugehört</h3>
              <ul className="mt-5 space-y-3">
                {notIncluded.map((n) => (
                  <li
                    key={n}
                    className="flex items-start gap-2.5 text-sm text-muted-foreground"
                  >
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                    {n}
                  </li>
                ))}
              </ul>
              <h3 className="mt-8 text-lg font-semibold">Was Sie bekommen</h3>
              <ul className="mt-5 space-y-3">
                {[
                  "Eine schriftliche Zielvereinbarung vor dem Start",
                  "Direkten Kontakt zu mir, ohne Projektmanager dazwischen",
                  "Messbare Zahlen statt Meinungen am Projektende",
                ].map((n) => (
                  <li key={n} className="flex items-start gap-2.5 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-3xl px-5 text-center">
            <h2 className="text-3xl font-bold md:text-4xl">
              Der erste Schritt ist Ihre Anfrage
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-muted-foreground">
              Schildern Sie kurz Ihr Vorhaben, ob neuer Shop, Anpassung oder Optimierung.
              Sie bekommen eine ehrliche Ersteinschätzung, kein Verkaufsgespräch.
            </p>
            <Link
              to="/kontakt"
              className="shadow-signal mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-4 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              Kostenlose Einschätzung erhalten
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
        <LeadSection source="methode" />
      </main>
      <SiteFooter />
    </div>
  );
}
